<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :style="{ width: '50rem' }"
    @update:visible="$emit('update:visible', $event)"
  >
    <template #header>
      <div class="flex w-full justify-between items-center">
        <div>
          <h3 class="text-xl font-semibold">
            Publish Changes
          </h3>
          <div v-if="lastPublishedAt" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Last published {{ lastPublishedTimeAgo }} ({{ lastPublishedFormatted }})
          </div>
        </div>
        <div class="flex space-x-4">
          <Button
            severity="secondary"
            label="Cancel"
            outlined
            @click="$emit('update:visible', false)"
          />
          <Button
            label="Publish"
            :loading="isPublishing"
            :disabled="isPublishing"
            @click="handlePublish"
          />
        </div>
      </div>
    </template>

    <div class="space-y-6">
      <!-- Changes Summary -->
      <div v-if="changesList.length > 0">
        <h4 class="text-base text-brand-headline font-semibold mb-3">
          What's Changed:
        </h4>
        <div class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div
            v-for="(categoryChanges, category) in groupedChanges"
            :key="category"
            class="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
          >
            <div class="bg-gray-50 dark:bg-gray-800 px-4 py-2">
              <div class="font-medium text-sm">
                {{ category }}
                <span class="text-gray-500 ml-2">
                  ({{ getCategorySummary(categoryChanges) }})
                </span>
              </div>
            </div>
            <div class="px-4 py-3 space-y-3">
              <div
                v-for="change in categoryChanges"
                :key="change.id"
                class="space-y-0"
              >
                <div class="flex items-center space-x-2 text-sm">
                  <i
                    :class="{
                      'pi pi-plus-circle text-green-600': change.type === 'added',
                      'pi pi-pencil text-blue-600': change.type === 'modified',
                      'pi pi-minus-circle text-red-600': change.type === 'deleted'
                    }"
                    class="text-xs"
                  />
                  <span class="text-brand-headline font-medium">{{ getCleanName(change) }}</span>
                  <span
                    v-if="change.type !== 'modified'"
                    class="text-xs text-gray-500 capitalize"
                  >
                    ({{ change.type }})
                  </span>
                </div>
                <div
                  v-if="isGeneratingMessage || aiDescriptions[change.id] || (change.details && change.details.length > 0)"
                  class="ml-5 text-xs text-brand-text/80"
                >
                  <Skeleton
                    v-if="isGeneratingMessage && !aiDescriptions[change.id]"
                    width="20rem"
                    height="0.75rem"
                  />
                  <span v-else>{{ getCleanDescription(change) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Commit Message -->
      <FormGroup label="Publish Message (Optional)">
        <Textarea
          v-model="publishMessage"
          :disabled="isGeneratingMessage"
          :placeholder="isGeneratingMessage ? 'Generating message...' : 'Describe what changed in this publish'"
          rows="6"
          class="w-full font-mono text-sm"
          :class="{ 'opacity-50': isGeneratingMessage }"
        />
        <template #description>
          <div class="flex items-center space-x-2 text-sm text-gray-500 mt-2">
            <i class="pi pi-info-circle" />
            <span>
              This message will be saved with your publish for future reference.
              {{ isGeneratingMessage ? 'Generating AI suggestion...' : '' }}
            </span>
          </div>
        </template>
      </FormGroup>

      <!-- Debug Section (Local Only) -->
      <FormGroup v-if="isLocal" label="Debug: Raw Diff (Local Only)">
        <Textarea
          :modelValue="rawDiffDebug"
          readonly
          rows="12"
          class="w-full font-mono text-xs"
        />
      </FormGroup>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import type { ConfigChange } from '@/composables/useConfigChanges'
import * as jsondiffpatch from 'jsondiffpatch'

const props = defineProps<{
  visible: boolean
  changes: ConfigChange[]
  isPublishing?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'publish', message: string): void
}>()

const { enrichChangesWithDescriptions } = useRightyHeadless()
const { stripHtmlFromDiff } = useConfigChanges()
const configStore = useConfigStore()

// Import stripHtml for cleaning HTML from display text
import { stripHtml } from '@/services/string'
const { publish, fullResponse, currentTeam } = storeToRefs(configStore)

const isLocal = import.meta.env.VITE_APP_ENV === 'development'

const publishMessage = ref('')
const isGeneratingMessage = ref(false)
const aiDescriptions = ref<Record<string, string>>({})

// Last published timestamp
const lastPublishedAt = computed(() => currentTeam.value?.last_published_at)
const lastPublishedTimeAgo = computed(() => {
  if (!lastPublishedAt.value) return ''
  return useTimeAgo(lastPublishedAt.value).value
})
const lastPublishedFormatted = computed(() => {
  if (!lastPublishedAt.value) return ''
  return useDateFormat(lastPublishedAt.value, 'MMM D, YYYY h:mm A').value
})

// Debug: Show raw diff for local development
const rawDiffDebug = computed(() => {
  if (!fullResponse.value) return 'No config loaded'

  const draft = fullResponse.value.draft
  const stable = fullResponse.value.stable

  if (!draft || !stable) return 'Missing draft or stable config'

  const diff = jsondiffpatch.diff(toRaw(stable), toRaw(draft))

  return JSON.stringify({
    hasDiff: !!diff,
    changes: props.changes,
    diff: diff
  }, null, 2)
})

const changesList = computed(() => props.changes || [])

const groupedChanges = computed(() => {
  return changesList.value.reduce((acc, change) => {
    if (!acc[change.category]) {
      acc[change.category] = []
    }
    acc[change.category].push(change)
    return acc
  }, {} as Record<string, ConfigChange[]>)
})

const getCategorySummary = (changes: ConfigChange[]): string => {
  const counts = changes.reduce((acc, change) => {
    acc[change.type] = (acc[change.type] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  const parts: string[] = []
  if (counts.added) parts.push(`${counts.added} added`)
  if (counts.modified) parts.push(`${counts.modified} modified`)
  if (counts.deleted) parts.push(`${counts.deleted} deleted`)

  return parts.join(', ')
}

// Helper to get clean display name without HTML tags
const getCleanName = (change: ConfigChange): string => {
  return stripHtml(change.name)
}

// Helper to get clean description without HTML tags
const getCleanDescription = (change: ConfigChange): string => {
  // If we have an AI description, use it (already clean)
  if (aiDescriptions.value[change.id]) {
    return aiDescriptions.value[change.id]
  }

  // Otherwise, strip HTML from details and join them
  if (change.details && change.details.length > 0) {
    return change.details.map(detail => stripHtml(detail)).join(', ')
  }

  return ''
}

const generateAIMessage = async () => {
  if (changesList.value.length === 0) return

  isGeneratingMessage.value = true
  try {
    // Convert reactive objects to plain objects, strip HTML from diffs
    const plainChanges = changesList.value.map(change => ({
      category: change.category,
      type: change.type,
      name: change.name,
      id: change.id,
      details: change.details,
      diff: change.diff ? stripHtmlFromDiff(toRaw(change.diff)) : undefined
    }))

    // Send changes with diffs to backend for AI enrichment
    const result = await enrichChangesWithDescriptions(plainChanges)

    // Store AI-generated descriptions keyed by item ID
    aiDescriptions.value = result.descriptions

    // Use AI-generated summary message
    publishMessage.value = result.summaryMessage
  } catch (error) {
    console.error('Failed to generate AI enrichment:', error)
    // Fallback to simple message
    publishMessage.value = `Update ${Object.keys(groupedChanges.value).length} configuration sections`
  } finally {
    isGeneratingMessage.value = false
  }
}

const handlePublish = () => {
  emit('publish', publishMessage.value)
}

// Auto-generate message when modal opens
watch(
  () => props.visible,
  (newVal) => {
    if (newVal && changesList.value.length > 0) {
      // Reset message and generate new one
      publishMessage.value = ''
      nextTick(() => {
        generateAIMessage()
      })
    }
  }
)
</script>
